import React, { useEffect, useState } from 'react';
import { portalService, PortalHistory } from '../services/portalService';

const PortalHistoryPage: React.FC = () => {
    const [history, setHistory] = useState<PortalHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'consultations' | 'diagnoses' | 'prescriptions'>('consultations');

    useEffect(() => {
        portalService
            .getHistory()
            .then(setHistory)
            .catch((err) => setError(err.response?.data?.message || 'No pudimos cargar tu historial clínico. Por favor, inténtalo de nuevo más tarde.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    if (!history) {
        return <div className="alert alert-info">No hay historial clínico disponible.</div>;
    }

    const tabs = [
        { key: 'consultations' as const, label: 'Consultas', icon: 'stethoscope', count: history.consultations.length },
        { key: 'diagnoses' as const, label: 'Diagnósticos', icon: 'notes-medical', count: history.diagnoses.length },
        { key: 'prescriptions' as const, label: 'Recetas', icon: 'prescription', count: history.prescriptions.length },
    ];

    return (
        <div className="portal-history">
            <h2 className="mb-4">
                <i className="fas fa-history me-2"></i>Mi Historial Clínico
            </h2>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4" role="tablist">
                {tabs.map((tab) => (
                    <li className="nav-item" key={tab.key}>
                        <button
                            className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            aria-controls={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <i className={`fas fa-${tab.icon} me-1`}></i>
                            {tab.label}
                            <span className="badge bg-secondary ms-2">{tab.count}</span>
                        </button>
                    </li>
                ))}
            </ul>

            {/* Consultations */}
            {activeTab === 'consultations' && (
                <div className="tab-content" id="consultations">
                    {history.consultations.length === 0 ? (
                        <div className="alert alert-info">No hay consultas registradas.</div>
                    ) : (
                        <div className="list-group">
                            {history.consultations.map((c) => (
                                <div key={c.id} className="list-group-item">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="mb-1">
                                                Consulta del {c.consultation_date}
                                            </h6>
                                            {c.doctor && (
                                                <small className="text-muted">
                                                    Dr. {c.doctor.first_name} {c.doctor.last_name}
                                                </small>
                                            )}
                                            {c.chief_complaint && (
                                                <p className="mb-1 mt-2">
                                                    <strong>Motivo:</strong> {c.chief_complaint}
                                                </p>
                                            )}
                                            {c.general_recommendations && (
                                                <p className="mb-0">
                                                    <strong>Recomendaciones:</strong> {c.general_recommendations}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Diagnoses */}
            {activeTab === 'diagnoses' && (
                <div className="tab-content" id="diagnoses">
                    {history.diagnoses.length === 0 ? (
                        <div className="alert alert-info">No hay diagnósticos registrados.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Consulta #</th>
                                        <th>Código</th>
                                        <th>Diagnóstico</th>
                                        <th>Tipo</th>
                                        <th>Observaciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.diagnoses.map((d) => (
                                        <tr key={d.id}>
                                            <td>{d.consultation_id}</td>
                                            <td>{d.diagnosis?.code || '—'}</td>
                                            <td>{d.diagnosis?.name || '—'}</td>
                                            <td>
                                                <span className="badge bg-info">{d.type || '—'}</span>
                                            </td>
                                            <td>{d.observations || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Prescriptions */}
            {activeTab === 'prescriptions' && (
                <div className="tab-content" id="prescriptions">
                    {history.prescriptions.length === 0 ? (
                        <div className="alert alert-info">No hay recetas registradas.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Consulta #</th>
                                        <th>Medicamento</th>
                                        <th>Dosis</th>
                                        <th>Frecuencia</th>
                                        <th>Duración</th>
                                        <th>Instrucciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.prescriptions.map((p) => (
                                        <tr key={p.id}>
                                            <td>{p.consultation_id}</td>
                                            <td>{p.medication_name}</td>
                                            <td>{p.dosage || '—'}</td>
                                            <td>{p.frequency || '—'}</td>
                                            <td>{p.duration || '—'}</td>
                                            <td>{p.instructions || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PortalHistoryPage;
